/** @jsxImportSource @emotion/react */
import { lazy, Suspense, type ReactNode } from 'react';
import { Switch as Routes, Route } from 'react-router-dom';
import Loader from '../commons/Loader';
import { RumErrorBoundary } from '../../observability/rum/RumErrorBoundary';
import RequireNonPatient from './RequireNonPatient';

// Route-level code splitting. Only one of these screens is ever on screen, and
// Settings alone pulls in recharts — which is why it stayed in the initial chunk
// even after the patient tabs were split. Declared below the imports because the
// eslint rule no-use-before-define counts `lazy` as a use.
const PatientDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "screen-patient-details" */ '../patient-details/PatientDetails'
    ),
);
const Emr = lazy(
  () => import(/* webpackChunkName: "screen-emr" */ '../emr/Emr'),
);
const Management = lazy(
  () =>
    import(
      /* webpackChunkName: "screen-management" */ '../management/Management'
    ),
);
const Extension = lazy(() =>
  import(
    /* webpackChunkName: "screen-extension" */ '../extension/Extension'
  ).then((m) => ({
    default: m.Extension,
  })),
);
const Settings = lazy(
  () =>
    import(/* webpackChunkName: "screen-settings" */ '../settings/Settings'),
);

// Per-route error boundary so a crash in one feature surfaces a labelled
// portal.error (with the breadcrumb trail) and isolates the failure to that
// route, instead of blanking the whole app via the app-root boundary.
function guardRoute(componentName: string, node: ReactNode): ReactNode {
  return (
    <RumErrorBoundary componentName={componentName}>{node}</RumErrorBoundary>
  );
}

export default function AppRoutes() {
  return (
    // Every screen below is lazy, so one boundary covers them all. The
    // fallback is the same Loader the "/" route already renders, so a cold
    // navigation looks like the existing loading state rather than a blank
    // frame.
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route exact path="/" render={() => <Loader />} />
        <Route
          path="/patient"
          render={() => guardRoute('patient-workspace', <PatientDetails />)}
        />
        <Route
          path="/patients"
          render={() =>
            guardRoute(
              'patient-management',
              <RequireNonPatient>
                <Management />
              </RequireNonPatient>,
            )
          }
        />
        <Route
          path="/clinicians"
          render={() =>
            guardRoute(
              'clinician-management',
              <RequireNonPatient>
                <Management />
              </RequireNonPatient>,
            )
          }
        />
        <Route
          path="/templates"
          render={() =>
            guardRoute(
              'templates',
              <RequireNonPatient>
                <Management />
              </RequireNonPatient>,
            )
          }
        />
        <Route
          path="/activities"
          render={() =>
            guardRoute(
              'custom-activities',
              <RequireNonPatient>
                <Management />
              </RequireNonPatient>,
            )
          }
        />
        <Route
          path="/emr"
          render={() =>
            guardRoute(
              'emr',
              <RequireNonPatient>
                <Emr />
              </RequireNonPatient>,
            )
          }
        />
        <Route
          path="/extension"
          render={() =>
            guardRoute(
              'extension',
              <RequireNonPatient>
                <Extension />
              </RequireNonPatient>,
            )
          }
        />
        <Route
          path="/settings"
          render={() =>
            guardRoute(
              'settings',
              <RequireNonPatient>
                <Settings />
              </RequireNonPatient>,
            )
          }
        />
      </Routes>
    </Suspense>
  );
}
